import moment from 'moment';
import React from 'react';
import { Link } from 'react-router-dom';

import BackgroundImage from '../../../../components/BackgroundImage';
import Img from '../../../../components/Img';
import { getNameInitials } from '../../../../utils/collegeCorner';
import { getRatingDetails, setRatingChoice } from '../../../../utils';
import { ratingTypes } from '../../../../utils/constants';
import { defaultTableFilters, SORT_ORDER } from '../helpers';
import { CPP_SORT_KEYS, getDefaultGenderFilter, getRecruitingStatusLabel, PLAYER_GENDERS, RECRUITING_STATUS_FILTER } from './cppConstants';
import MoveToRecruitsCell from './MoveToRecruitsCell';
import { getNationalityName } from '../../../../utils/nationality';
import styles from './CppJuniorProspects.scss';

const EM_DASH = '—';
const DEFAULT_PAGE_SIZE = 25;
const PLAYER_PROFILE_PATH = '/profiles';
const TESTID_PREFIX = 'college-corner.junior-prospects';
const SINGLES_UTR_ICON = '/img/v2/icons/utr-user-dark.svg';
const DOUBLES_UTR_ICON = '/img/v2/icons/utr-users-dark.svg';
const VERIFIED_ICON = '/img/v4/icon-verified-checkmark-black.svg';

const utrTitle = (iconSrc, testId) => (
    <div className={`${styles.TextWithIcon}`}>
        <span>{ratingTypes.tennis}</span>
        <span className={`${styles.icon}`}>
            <img src={iconSrc} className="img-fluid" alt="" data-testid={testId} />
        </span>
    </div>
);
const UNCLAIMED_ROW_MESSAGE =
    'This player hasn\'t claimed their UTR profile yet, so they can\'t be contacted or added.';

const getRowTestId = (item, extra) => `${TESTID_PREFIX}.row-${item?.id ?? extra?.rowIndex}`;

export const getCppInitialFilters = (collegeCoachTeamGenders = []) => ({
    ...defaultTableFilters,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: CPP_SORT_KEYS.SINGLES_UTR,
    order: SORT_ORDER.DESC,
    gender: getDefaultGenderFilter(collegeCoachTeamGenders),
    locationInputValue: '',
    locationCountryName: '',
    locationStateName: '',
    distance: '',
    location: '',
    lat: null,
    lng: null,
    nationality: '',
    sportTypes: { tennis: true },
    utrMin: null,
    utrMax: null,
    utrType: 'verified',
    utrTeamType: 'singles',
    utrReliability: { rated: true },
    hsGradYears: '',
    recruitingStatus: RECRUITING_STATUS_FILTER.ALL,
});

const genderLabel = (gender) => {
    if (gender === PLAYER_GENDERS.MALE) {
        return 'M';
    }
    if (gender === PLAYER_GENDERS.FEMALE) {
        return 'F';
    }
    return gender || '';
};

const getCppUtrRating = (player, ratingType) => getRatingDetails(
    setRatingChoice({ player, ratingType, utrType: 'sanctioned' }),
    false,
    false,
    false,
    false,
);

const renderUtrCell = (item, ratingType, dataTestId) => {
    const rating = getCppUtrRating(item?.source || {}, ratingType);
    if (!rating?.value) {
        return '';
    }
    return (
        <div className={`${styles.utrValueWrap}`} data-testid={dataTestId || undefined}>
            <div className={`${styles.ratings}`}>
                <span data-testid={dataTestId && `${dataTestId}.value`}>{rating.value}</span>
                {rating.verified && (
                    <Img src={VERIFIED_ICON} alt="Verified" className={`${styles.verifiedIcon}`} data-testid={dataTestId && `${dataTestId}.verified`} />
                )}
            </div>
            {rating.progress ? (
                <span className={`${styles.reliability}`} data-testid={dataTestId && `${dataTestId}.reliability`}>{`${rating.progress}% Reliable`}</span>
            ) : null}
        </div>
    );
};

const emptyValue = (dataTestId) => (
    <span className={`${styles.emptyValue}`} data-testid={dataTestId || undefined}>{EM_DASH}</span>
);

const isMissingValue = (value) => value === null || value === undefined || value === '';

const renderGradYear = (item) => {
    const gradYear = item?.source?.playerHighSchoolDetails?.gradYear;
    return gradYear ? moment(gradYear).format('YYYY') : emptyValue();
};

const renderAcademicInfo = (cpp, dataTestId) => {
    if (!cpp) {
        return emptyValue(dataTestId);
    }
    const fields = [
        ['GPA', cpp.highSchoolGpa],
        ['SAT', cpp.satScore],
        ['Major', cpp.majorOfInterest],
    ];
    return (
        <div className={`${styles.academicInfo}`} data-testid={dataTestId || undefined}>
            {fields.map(([label, value], index) => (
                <span key={label} className={`${styles.academicLine}`} data-testid={dataTestId && `${dataTestId}.line-${index}`}>
                    {`${label}: `}
                    {isMissingValue(value) ? emptyValue() : value}
                </span>
            ))}
        </div>
    );
};

const renderPlayerCell = (item, dataTestId) => {
    const source = item?.source || {};
    const isClaimed = !!item?.cpp && !!source.memberId;
    const playerName = source.displayName || '';
    const label = genderLabel(source.gender);
    const location = source.location?.display || '';
    const nationalFlag = source.nationality
        ? `/img/flags/${source.nationality.toLowerCase()}.svg`
        : '';
    return (
        <div
            className={`${styles.playerCell} ${isClaimed ? '' : styles.unclaimed}`}
            title={isClaimed ? undefined : UNCLAIMED_ROW_MESSAGE}
            data-testid={dataTestId || undefined}
        >
            <div className={`${styles.displayLogo}`}>
                {source.playerProfileImages?.default ? (
                    <BackgroundImage
                        baseUrl={'/v1/player/'}
                        defaultImage={'/img/v2/icons/icon-avatar-player.svg'}
                        images={source.playerProfileImages}
                        imageType={'icon'}
                        parentStylesClass={styles.profilePicture}
                    />
                ) : (
                    <div className="avatar-placeholder" data-testid={dataTestId && `${dataTestId}.initials`}>
                        {getNameInitials(source)}
                    </div>
                )}
            </div>
            <div className={`${styles.playerMeta}`}>
                <Link className="link" to={`${PLAYER_PROFILE_PATH}/${item.id}`} data-testid={dataTestId && `${dataTestId}.profile-link`}>
                    {playerName}
                </Link>
                <div className={`${styles.playerSubline}`}>
                    {label && <span className={`${styles.genderLabel}`} data-testid={dataTestId && `${dataTestId}.gender`}>{label}</span>}
                    {label && location && <span className={`${styles.dot}`} data-testid={dataTestId && `${dataTestId}.separator`}>•</span>}
                    {location && <span className={`${styles.location}`} data-testid={dataTestId && `${dataTestId}.location`}>{location}</span>}
                </div>
                {source.nationality && (
                    <div className={`${styles.nationality}`}>
                        <Img className={`${styles.flag}`} src={nationalFlag} alt={source.nationality} data-testid={dataTestId && `${dataTestId}.flag`} />
                        <span className={`${styles.nationalityName}`} data-testid={dataTestId && `${dataTestId}.nationality-name`}>{getNationalityName(source.nationality)}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export const getCppColumns = ({ collegeCoachTeamGenders = [] } = {}) => ([
    {
        title: 'Name',
        sortable: true,
        key: CPP_SORT_KEYS.NAME,
        maxWidth: 28,
        headerClassName: `${styles.nameHead}`,
        rowClassName: `${styles.nameCol}`,
        custom: (val, item, extra) => renderPlayerCell(item, `${getRowTestId(item, extra)}.player`),
    },
    {
        title: utrTitle(SINGLES_UTR_ICON, `${TESTID_PREFIX}.singles-utr-header.icon`),
        sortable: true,
        key: CPP_SORT_KEYS.SINGLES_UTR,
        maxWidth: 11,
        headerClassName: `${styles.utrHead}`,
        rowClassName: `${styles.utrCol}`,
        custom: (val, item, extra) => renderUtrCell(item, 'singles', `${getRowTestId(item, extra)}.singles-utr`),
    },
    {
        title: utrTitle(DOUBLES_UTR_ICON, `${TESTID_PREFIX}.doubles-utr-header.icon`),
        sortable: false,
        key: CPP_SORT_KEYS.DOUBLES_UTR,
        maxWidth: 11,
        headerClassName: `${styles.utrTwoHead}`,
        rowClassName: `${styles.utrTwoCol}`,
        custom: (val, item, extra) => renderUtrCell(item, 'doubles', `${getRowTestId(item, extra)}.doubles-utr`),
    },
    {
        title: 'Grad Yr',
        sortable: true,
        key: CPP_SORT_KEYS.GRAD_YEAR,
        maxWidth: 8,
        headerClassName: `${styles.gradYearHead}`,
        rowClassName: `${styles.gradYearCol}`,
        custom: (val, item) => renderGradYear(item),
    },
    {
        title: 'Academic Info',
        sortable: false,
        key: 'academicInfo',
        maxWidth: 16,
        headerClassName: `${styles.academicHead}`,
        rowClassName: `${styles.academicCol}`,
        custom: (val, item, extra) => renderAcademicInfo(item?.cpp, `${getRowTestId(item, extra)}.academic`),
    },
    {
        title: 'Status',
        sortable: false,
        key: 'recruitingStatus',
        maxWidth: 11,
        headerClassName: `${styles.statusHead}`,
        rowClassName: `${styles.statusCol}`,
        custom: (val, item) => getRecruitingStatusLabel(item?.cpp) || emptyValue(),
    },
    {
        title: 'Agency',
        sortable: false,
        key: 'agency',
        maxWidth: 11,
        headerClassName: `${styles.agencyHead}`,
        rowClassName: `${styles.agencyCol}`,
        custom: (val, item) => item?.cpp?.agencyName || emptyValue(),
    },
    {
        title: '',
        sortable: false,
        key: 'contact',
        maxWidth: 14,
        headerClassName: `${styles.contactHead}`,
        rowClassName: `${styles.contactCol}`,
        custom: (val, item, extra) => (
            <MoveToRecruitsCell
                item={item}
                collegeCoachTeamGenders={collegeCoachTeamGenders}
                data-testid={getRowTestId(item, extra)}
            />
        ),
    },
]);
